// La hoja de vista previa (04-11-2026; web/qa/auditoria-apple/propuesta-2/fuentes/pdf.md §3, maqueta visor-despues.png).
// Como Vista Rápida de Apple: un PDF, una imagen, un informe o un texto se abren encima del chat en UNA hoja oscura —en el
// PC a 24 px de los bordes, en el iPad a pantalla completa—, sin desenfoque (parpadea en Chrome/Windows, D-037).
//
// Por qué así (lo que se quita): el PDF se veía con el lector del navegador metido en un <iframe> —dos barras, dos
// «Descargar», herramientas que Pau no usa, una página que no cabía, un blanco al cargar y, sobre todo, Escape dejaba de
// cerrar en cuanto Pau pinchaba dentro (el iframe se quedaba las teclas)—, y había tres visores distintos (imagen,
// documento, informe). Ahora las páginas las pinta la oficina (pdf.js, VisorPdf.tsx) y las teclas son siempre nuestras.
//
// Teclado: Esc cierra SIEMPRE (Radix, en el documento) · ←/→, AvPág/RePág: página o imagen anterior/siguiente ·
// Inicio/Fin · ↑/↓/Espacio: desplazar · + / − / 0: acercar / alejar / ajustar · Ctrl+rueda y pellizco: tamaño. El foco
// se queda en la hoja y al cerrar vuelve a lo que se pulsó (la tarjeta). Cada nivel de la pila es su propio diálogo:
// una imagen abierta desde un informe se cierra sola y deja el informe donde estaba.
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Download, ExternalLink, FileText, Minus, PanelLeft, Plus, X } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type KeyboardEvent as KeyboardEventReact } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime, formatFechaHora } from '../../../lib/time';
import { IconoEncargo, Pista } from '../ui';
import { BOTON, BotonBarra, EnlaceIcono, type ControlCuerpo, type PropsCuerpo } from './piezas';
import { useVistaPrevia, type Apertura, type ElementoVista } from './vistaPrevia';
import { VisorImagenes } from './VisorImagenes';
import { VisorPdf } from './VisorPdf';
import { VisorTexto } from './VisorTexto';
import { acercar, alejar, etiquetaZoom, type Zoom } from './zoom';

/** Monta una hoja por cada nivel de la pila. Va una vez en la página de la oficina. */
export function HojaVistaPrevia() {
  const pila = useVistaPrevia((s) => s.pila);
  return (
    <>
      {pila.map((a, i) => (
        <Hoja key={i} apertura={a} />
      ))}
    </>
  );
}

const CON_ZOOM = new Set(['pdf', 'imagen']);

// Con ratón y pantalla ancha el carril de páginas sale abierto; con el dedo (iPad) va detrás de «Páginas».
const carrilPorDefecto = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine) and (min-width: 1024px)').matches;

function Hoja({ apertura }: { apertura: Apertura }) {
  const cerrar = useVistaPrevia((s) => s.cerrar);
  const elemento = apertura.elementos[apertura.indice] ?? apertura.elementos[0]!;
  const [zoom, setZoom] = useState<Zoom>('ajustar');
  const [carril, setCarril] = useState(carrilPorDefecto);
  const control = useRef<ControlCuerpo>(null);
  // Lo que tenía el foco al abrir (la tarjeta, la miniatura): vuelve ahí al cerrar. Radix solo lo hace con su
  // Dialog.Trigger, y la hoja se abre desde muchos sitios sin él.
  const previo = useRef(typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null));
  // Cambiar de imagen vuelve a «Ajustar» (como Vista Rápida).
  useEffect(() => {
    setZoom('ajustar');
  }, [elemento.clave]);

  const conZoom = CON_ZOOM.has(elemento.clase);
  const ajuste = () => control.current?.ajuste() ?? 100;
  const conCarril = elemento.clase === 'pdf' || (elemento.clase === 'imagen' && apertura.elementos.length > 1);

  const alTeclear = (e: KeyboardEventReact<HTMLDivElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const c = control.current;
    const enBoton = (e.target as HTMLElement).closest('button, a, input, textarea, select');
    const hacer = (fn: () => void) => {
      e.preventDefault();
      fn();
    };
    switch (e.key) {
      case 'ArrowLeft':
      case 'PageUp':
        return c && hacer(c.anterior);
      case 'ArrowRight':
      case 'PageDown':
        return c && hacer(c.siguiente);
      case 'Home':
        return c && hacer(c.inicio);
      case 'End':
        return c && hacer(c.fin);
      case '+':
      case '=':
        return conZoom && hacer(() => setZoom(acercar(zoom, ajuste())));
      case '-':
      case '_':
        return conZoom && hacer(() => setZoom(alejar(zoom, ajuste())));
      case '0':
        return conZoom && hacer(() => setZoom('ajustar'));
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ': {
        // Con el foco en un botón de la barra, el Espacio es del botón; las flechas desplazan igualmente.
        const zona = c?.desplazable();
        if (!zona || zona.contains(e.target as Node) || (enBoton && e.key === ' ')) return;
        const paso = e.key === ' ' ? zona.clientHeight * 0.85 : 64;
        return hacer(() => zona.scrollBy({ top: e.key === 'ArrowUp' ? -paso : paso }));
      }
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(abierta) => !abierta && cerrar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/80 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-testid="vista-previa"
          data-clase={elemento.clase}
          onOpenAutoFocus={(e) => {
            // El foco va a lo que se lee (flechas y Espacio desplazan desde el primer momento), no a la ✕.
            e.preventDefault();
            const raiz = e.currentTarget as HTMLElement;
            (raiz.querySelector<HTMLElement>('[data-autofoco]') ?? raiz).focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (previo.current?.isConnected) previo.current.focus({ preventScroll: true });
          }}
          onKeyDown={alTeclear}
          tabIndex={-1}
          className={cn(
            'fixed inset-6 z-[61] flex flex-col overflow-hidden rounded-o-xl bg-oficina-panel text-oficina-texto shadow-o-hoja outline-none',
            '[@media(pointer:coarse)]:inset-0 [@media(pointer:coarse)]:rounded-none',
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-[0.98] motion-safe:duration-200 motion-safe:ease-o-apple',
          )}
        >
          <Barra
            apertura={apertura}
            elemento={elemento}
            zoom={conZoom ? { valor: zoom, alCambiar: setZoom, ajuste } : null}
            carril={conCarril ? { abierto: carril, alCambiar: setCarril } : null}
          />
          <Cuerpo ref={control} apertura={apertura} elemento={elemento} zoom={zoom} alZoom={setZoom} carril={conCarril && carril} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Barra ────────────────────────────────────────────────────────────────

/** «hoy, 16:32» o «3 nov, 16:32». */
function cuando(en: string): string {
  const d = new Date(en);
  if (Number.isNaN(d.getTime())) return '';
  return d.toDateString() === new Date().toDateString() ? t('oficinaV2.vistaPrevia.hoy', { hora: formatClockTime(en) }) : formatFechaHora(en);
}

function Barra({
  apertura,
  elemento,
  zoom,
  carril,
}: {
  apertura: Apertura;
  elemento: ElementoVista;
  zoom: { valor: Zoom; alCambiar: (z: Zoom) => void; ajuste: () => number } | null;
  carril: { abierto: boolean; alCambiar: (v: boolean) => void } | null;
}) {
  const quien = [apertura.autor?.nombre, apertura.en ? cuando(apertura.en) : null, elemento.detalle || null].filter(Boolean).join(' · ');
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-oficina-linea px-4 [@media(pointer:coarse)]:h-16">
      {carril ? (
        <BotonBarra icono={PanelLeft} etiqueta={t(elemento.clase === 'pdf' ? 'oficinaV2.vistaPrevia.paginas' : 'oficinaV2.vistaPrevia.imagenes')} activo={carril.abierto} onClick={() => carril.alCambiar(!carril.abierto)} />
      ) : null}
      {apertura.autor?.semilla ? (
        <IconoEncargo icono={apertura.autor.icono ?? null} semilla={apertura.autor.semilla} tamano={32} />
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-o-s bg-oficina-control text-oficina-texto2">
          <FileText className="h-4 w-4" aria-hidden />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <DialogPrimitive.Title className="truncate text-o-texto font-semibold text-oficina-texto">{elemento.titulo}</DialogPrimitive.Title>
        {quien ? <p className="truncate text-o-nota text-oficina-texto3">{quien}</p> : null}
      </div>
      {zoom ? (
        <div role="group" aria-label={t('oficinaV2.vistaPrevia.zoom')} className="flex shrink-0 items-center rounded-full bg-oficina-control">
          <BotonBarra icono={Minus} etiqueta={t('oficinaV2.vistaPrevia.alejar')} onClick={() => zoom.alCambiar(alejar(zoom.valor, zoom.ajuste()))} />
          <Pista texto={t('oficinaV2.vistaPrevia.ajustarPista')}>
            <button
              type="button"
              onClick={() => zoom.alCambiar('ajustar')}
              className="h-9 min-w-[4.5rem] px-2 text-o-sub font-medium tabular-nums text-oficina-texto outline-none focus-visible:ring-2 focus-visible:ring-oficina-texto [@media(pointer:coarse)]:h-touch-target"
            >
              {etiquetaZoom(zoom.valor, t('oficinaV2.vistaPrevia.ajustar'))}
            </button>
          </Pista>
          <BotonBarra icono={Plus} etiqueta={t('oficinaV2.vistaPrevia.acercar')} onClick={() => zoom.alCambiar(acercar(zoom.valor, zoom.ajuste()))} />
        </div>
      ) : null}
      <EnlaceIcono icono={ExternalLink} etiqueta={t('oficinaV2.vistaPrevia.abrirFuera')} href={elemento.url} target="_blank" rel="noopener noreferrer" />
      <EnlaceIcono icono={Download} etiqueta={t('oficinaV2.vistaPrevia.descargar')} href={elemento.url} download={elemento.nombre} />
      <span className="h-5 w-px shrink-0 bg-oficina-linea" aria-hidden />
      <Pista texto={t('oficinaV2.vistaPrevia.cerrar')}>
        <DialogPrimitive.Close aria-label={t('oficinaV2.vistaPrevia.cerrar')} className={cn(BOTON, 'bg-oficina-control text-oficina-texto hover:bg-oficina-pulsado')}>
          <X className="h-[18px] w-[18px]" strokeWidth={2.5} aria-hidden />
        </DialogPrimitive.Close>
      </Pista>
    </header>
  );
}

// ── Cuerpo ───────────────────────────────────────────────────────────────

const Cuerpo = forwardRef<ControlCuerpo, PropsCuerpo>(function Cuerpo(props, ref) {
  switch (props.elemento.clase) {
    case 'pdf':
      return <VisorPdf key={props.elemento.clave} ref={ref} {...props} />;
    case 'imagen':
      return <VisorImagenes ref={ref} {...props} />;
    default:
      return <VisorTexto key={props.elemento.clave} ref={ref} {...props} />;
  }
});

/** Píldora flotante «‹ 1 de 11 ›» (abajo, en el centro). Se esconde a los 2 s sin mover nada y vuelve al moverse. */